import { useMemo } from 'react'
import { EntityDetail } from '@user/store/map/typings'
import { LocationDTO } from '@sales-center/common'
import { useAppSelector } from '@user/store/hooks/useAppSelector'
import { selectServiceLocationId } from '@user/store/map/slice'
import { hasViewableHistory } from '@user/utils/knockStatusFormatting'

type Props = {
  entityDetails: EntityDetail | null
  variant: string
  serviceLocation: LocationDTO
}

const mapVariantToEntityType = (variant: string | null): string => {
  switch (variant?.toLowerCase()) {
    case 'opportunity':
      return 'Opportunity'
    case 'lead':
      return 'Lead'
    case 'pendinglead':
    case 'draftlead':
      return 'DraftLead'
    default:
      return 'Unknown'
  }
}

export function useData({ entityDetails, variant, serviceLocation }: Props) {
  const selectedServiceLocationId = useAppSelector(selectServiceLocationId)

  const isDraftLead =
    variant?.toLowerCase() === 'draftlead' ||
    variant?.toLowerCase() === 'pendinglead'

  const entityType = useMemo(() => mapVariantToEntityType(variant), [variant])

  const knockStatusLocationId = useMemo(() => {
    if (isDraftLead) {
      return entityDetails?.knockStatusDetails?.knockStatusLocationId ?? null
    }
    return serviceLocation?.knockStatusDetails?.knockStatusLocationId ?? null
  }, [entityDetails, isDraftLead, serviceLocation])

  const knockStatusHistoryId = useMemo(() => {
    if (isDraftLead) {
      return entityDetails?.knockStatusDetails?.id ?? null
    }
    return serviceLocation?.knockStatusDetails?.id ?? null
  }, [entityDetails, isDraftLead, serviceLocation])

  const knockStatusCount = useMemo(() => {
    if (isDraftLead) {
      return entityDetails?.knockStatusDetails?.knockStatusCount ?? 0
    }
    return serviceLocation?.knockStatusDetails?.knockStatusCount ?? 0
  }, [entityDetails, isDraftLead, serviceLocation])

  const knockStatus = useMemo(() => {
    return {
      isDefault:
        serviceLocation?.knockStatusDetails?.knockStatusSettingsName ===
        'Do not knock',
      status:
        serviceLocation?.knockStatusDetails?.knockStatusSettingsName || '',
      note: serviceLocation?.knockStatusDetails?.mostRecentNote || null,
    }
  }, [serviceLocation])

  const knockStatusCreatedTime = useMemo(() => {
    if (isDraftLead) {
      return entityDetails?.knockStatusDetails?.createdTime ?? null
    }
    return serviceLocation?.knockStatusDetails?.createdTime ?? null
  }, [entityDetails, isDraftLead, serviceLocation])

  const knockStatusCreatedByName = useMemo(() => {
    if (isDraftLead) {
      return entityDetails?.knockStatusDetails?.createdByName ?? null
    }
    return serviceLocation?.knockStatusDetails?.createdByName ?? null
  }, [entityDetails, isDraftLead, serviceLocation])

  const hasKnockStatus = knockStatusCount > 0

  // Resolved like knockStatusCount above, not from knockStatus.status: that one is always
  // serviceLocation-derived, so pairing it with a draft lead's entityDetails count would report
  // "no active status" for a draft whose status is live.
  const activeKnockStatusName = useMemo(() => {
    if (isDraftLead) {
      return entityDetails?.knockStatusDetails?.knockStatusSettingsName ?? null
    }
    return serviceLocation?.knockStatusDetails?.knockStatusSettingsName ?? null
  }, [entityDetails, isDraftLead, serviceLocation])

  // SH-1550 -- history outlives the deletion of its setting, so the link shows with no active status.
  const canViewHistory = hasViewableHistory(
    knockStatusCount,
    Boolean(activeKnockStatusName),
  )

  // hasActiveStatus gates the clear (trash) action; draft-aware, unlike knockStatus.status.
  return {
    selectedServiceLocationId: selectedServiceLocationId ?? 0,
    isDraftLead,
    entityType,
    knockStatusLocationId,
    knockStatusHistoryId,
    knockStatusCount,
    knockStatus,
    hasKnockStatus,
    hasActiveStatus: Boolean(activeKnockStatusName),
    canViewHistory,
    knockStatusCreatedTime,
    knockStatusCreatedByName,
  }
}
